import { z } from 'zod';
import { Clan } from './clans';
import { PaginatedResponse } from './api';
import { Badge } from './badges';
import { Permission, permissions } from './permissions';

export const roleSchema = z.object({
    id: z.number(),
    privilege_level: z.number(),
    role_desc: z.string(),
    hide: z.boolean(),
});

export type Role = z.input<typeof roleSchema>;

export const baseUserSchema = z.object({
    id: z.string(),
    username: z.string(),
    global_name: z.string(),
});

export type BaseUser = z.infer<typeof baseUserSchema>;

export const extendedBaseUserSchema = baseUserSchema.extend({
    country: z.number().nullable(),
    discord_id: z.string().nullable(),
    discord_avatar: z.string().nullable(),
    discord_avatar_decoration: z.string().nullable(),
    featured_badge_code: z.string().nullable(),
});

export type ExtendedBaseUser = z.infer<typeof extendedBaseUserSchema>;

export const userSchema = extendedBaseUserSchema.extend({
    placeholder: z.boolean(),
    ban_level: z.number(),
    description: z.string().nullable(),
    background_level: z.number(),
    created_at: z.string(),
});

export type User = z.infer<typeof userSchema>;
export interface UserResolved extends User {
    clan?: Clan;
    roles: Role[];
    scopes: Permission[];
    badges: Badge[];
}

export const roleResolvedSchema = roleSchema.extend({
    inherits_from_role: roleSchema.nullable(),
    users: z.array(baseUserSchema),
    permissions: z.array(z.enum(permissions)),
});
export type RoleResolved = z.infer<typeof roleResolvedSchema>;

export interface UpdateUser {
    global_name?: string;
    discord_id?: string;
    description?: string;
    country?: number;
    ban_level?: number;
    username?: string;
}

export type UsersResponse = PaginatedResponse<User>;

export interface Notification {
    id: string;
    user_id: string;
    content: string;
    notification_type: 'Info' | 'Success' | 'Failure';
    created_at: string;
}

export type OAuthProvider = 'Patreon' | 'Discord' | 'Google';

export interface ConnectedAccount {
    id: string;
    user_id: string;
    provider: OAuthProvider;
    provider_user_id: string;
    provider_user_name: string;
    created_at: string;
}
